/**
 * AdminNewIdentityPage.jsx  —  /admin/students/new-identity
 *
 * Tek sayfa kimlik oluşturma formu.
 * Lead prefill: ?prefill_lead=<id>&ad_soyad=...&telefon=...&email=...
 * Başarı ekranında: öğrenci no, geçici şifre, onay kodu + aksiyon butonları.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage, getApiErrorCode } from '../utils/apiError';
import {
  CheckCircle2,
  Copy,
  Send,
  UserSquare2,
  ShieldCheck,
  AlertTriangle,
  MessageSquare,
  ExternalLink,
  Plus,
  Loader2,
  ShoppingCart,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_CLS =
  'w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none transition-colors';
const LBL_CLS = 'text-[11px] font-bold text-[var(--text-muted)] mb-1 block uppercase tracking-wide';
const SECTION_CLS = 'card-clay p-5 space-y-4';

// ── Small helpers ─────────────────────────────────────────────────────────────

function Field({ label, children, required, error, hint }) {
  return (
    <div>
      <label className={LBL_CLS}>
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-[10px] text-[var(--text-muted)] mt-1">{hint}</p>}
      {error && <p className="text-[10px] text-rose-600 mt-1 font-bold">{error}</p>}
    </div>
  );
}

function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <motion.button
      type="button"
      onClick={copy}
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.96 }}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-[var(--border-light)] text-xs font-bold hover:bg-gray-50"
    >
      <Copy size={13} className={copied ? 'text-emerald-500' : 'text-[var(--text-muted)]'} />
      {copied ? (label === undefined ? 'Kopyalandı' : label) : (label === undefined ? 'Kopyala' : label)}
    </motion.button>
  );
}

// ── Success Card ──────────────────────────────────────────────────────────────

function SuccessCard({ result, lang, authHeaders, onNewRecord, navigate }) {
  const tr = (t, e) => (lang === 'tr' ? t : e);
  const toast = useToast();
  const [sending, setSending] = useState({});
  const [sent, setSent] = useState({});

  const sendApprovalCode = async () => {
    if (sending.approval || sent.approval) return;
    setSending((s) => ({ ...s, approval: true }));
    try {
      await axios.post(
        `${API}/admin/students/${result.user_id}/send-approval-code`,
        {},
        { headers: authHeaders() }
      );
      toast.success(tr('Onay kodu SMS ile gönderildi', 'Approval code sent via SMS'));
      setSent((s) => ({ ...s, approval: true }));
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSending((s) => ({ ...s, approval: false }));
    }
  };

  const sendCredentials = async () => {
    if (sending.creds || sent.creds) return;
    setSending((s) => ({ ...s, creds: true }));
    try {
      await axios.post(
        `${API}/admin/students/${result.user_id}/send-credentials`,
        {},
        { headers: authHeaders() }
      );
      toast.success(tr('Giriş bilgileri SMS ile gönderildi', 'Login credentials sent via SMS'));
      setSent((s) => ({ ...s, creds: true }));
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSending((s) => ({ ...s, creds: false }));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="max-w-xl mx-auto space-y-5"
    >
      {/* Header */}
      <div className="text-center py-6">
        <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-3"
          style={{ boxShadow: '0 4px 0 0 #059669' }}>
          <CheckCircle2 size={32} className="text-white" strokeWidth={2.5} />
        </div>
        <h2 className="font-display text-2xl font-bold text-[var(--text-main)] mb-1">
          {tr('Kimlik Oluşturuldu!', 'Identity Created!')}
        </h2>
        <p className="text-sm text-[var(--text-muted)]">
          {tr('Öğrenci sisteme başarıyla kaydedildi.', 'Student successfully registered in the system.')}
        </p>
      </div>

      {/* Credentials card */}
      <div className="card-clay p-5 space-y-4">
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl px-4 py-2.5 flex items-center gap-2 text-amber-700 text-xs font-bold">
          <AlertTriangle size={14} />
          {tr(
            'Aşağıdaki bilgiler bir daha gösterilmeyecek. Lütfen kopyalayın veya SMS gönderin.',
            'The information below will not be shown again. Please copy or send via SMS.'
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="p-3 rounded-xl border-2 border-[var(--border-light)] bg-white">
            <p className={LBL_CLS}>{tr('Öğrenci No', 'Student No')}</p>
            <p className="font-mono font-bold text-base text-[var(--text-main)]">{result.ogrenci_kodu}</p>
          </div>
          <div className="p-3 rounded-xl border-2 border-[var(--border-light)] bg-white relative">
            <p className={LBL_CLS}>{tr('Geçici Şifre', 'Temp Password')}</p>
            <p className="font-mono font-bold text-base text-[var(--text-main)]">{result.gecici_sifre}</p>
          </div>
          <div className="p-3 rounded-xl border-2 border-[var(--border-light)] bg-white">
            <p className={LBL_CLS}>{tr('Onay Kodu', 'Approval Code')}</p>
            <p className="font-mono font-bold text-base text-[var(--text-main)]">{result.onay_kodu}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <CopyButton value={result.ogrenci_kodu} label={tr('Öğrenci No', 'Student No')} />
          <CopyButton value={result.gecici_sifre} label={tr('Şifre', 'Password')} />
          <CopyButton value={result.onay_kodu} label={tr('Onay Kodu', 'Approval Code')} />
          <CopyButton
            value={`${tr('Öğrenci No', 'Student No')}: ${result.ogrenci_kodu}\n${tr('Şifre', 'Password')}: ${result.gecici_sifre}\n${tr('Onay Kodu', 'Approval')}: ${result.onay_kodu}`}
            label={tr('Tümünü Kopyala', 'Copy All')}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="card-clay p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <motion.button
          type="button"
          onClick={sendApprovalCode}
          disabled={!!sending.approval || !!sent.approval}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 border-[var(--brand-primary)] text-[var(--brand-primary)] text-sm font-bold hover:bg-cyan-50 disabled:opacity-50"
        >
          {sending.approval ? <Loader2 size={15} className="animate-spin" /> : <MessageSquare size={15} />}
          {sent.approval
            ? tr('Gönderildi ✓', 'Sent ✓')
            : tr('Onay Kodunu SMS Gönder', 'Send Approval Code SMS')}
        </motion.button>

        <motion.button
          type="button"
          onClick={sendCredentials}
          disabled={!!sending.creds || !!sent.creds}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 border-emerald-500 text-emerald-600 text-sm font-bold hover:bg-emerald-50 disabled:opacity-50"
        >
          {sending.creds ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          {sent.creds
            ? tr('Gönderildi ✓', 'Sent ✓')
            : tr('Giriş Bilgilerini SMS Gönder', 'Send Login Credentials SMS')}
        </motion.button>

        <motion.button
          type="button"
          onClick={() => navigate(`/admin/students/${result.user_id}/sale`)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-bold border-b-4 border-emerald-700 hover:opacity-90 sm:col-span-2"
          data-testid="go-to-sale-btn"
        >
          <ShoppingCart size={15} />
          {tr('Satışa Geç', 'Start Sale')}
        </motion.button>

        <motion.button
          type="button"
          onClick={() => navigate(`/admin/students/${result.user_id}`)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] text-[var(--text-main)] text-sm font-bold hover:bg-gray-50"
        >
          <ExternalLink size={15} />
          {tr('Öğrenci Profiline Git', 'Go to Student Profile')}
        </motion.button>

        <motion.button
          type="button"
          onClick={onNewRecord}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold border-b-4 border-[var(--brand-primary-shadow)] hover:opacity-90"
        >
          <Plus size={15} />
          {tr('Yeni Kayıt', 'New Record')}
        </motion.button>
      </div>
    </motion.div>
  );
}

// ── Main Form ─────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  ad: '',
  soyad: '',
  tc_vatandasi: true,
  tc_no: '',
  pasaport_no: '',
  dogum_tarihi: '',
  dogum_yeri: '',
  anne_adi: '',
  baba_adi: '',
  cinsiyet: '',
  email: '',
  telefon: '',
  telefon2: '',
  yurtdisi_no: '',
  yurtdisi_tel: false,
  sehir: '',
  adres: '',
  meslek: '',
  egitim_durumu: '',
  lisans_bolumu: '',
  egitim_aciklama: '',
  reklam_turu: '',
  ogrenci_kodu_ozel: '',
  aktiflik_suresi: '',
  aciklama: '',
  pasif: false,
  fatura_talebi: false,
  lead_id: null,
};

export default function AdminNewIdentityPage() {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const tr = (t, e) => (lang === 'tr' ? t : e);

  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successResult, setSuccessResult] = useState(null);

  // Identity verify state
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null); // { verified, method, note }

  // Definitions
  const [defs, setDefs] = useState({
    meslek: [], sehir: [], reklam_turu: [], egitim_durumu: [],
  });

  // Lead prefill — tam fetch + query-string fallback
  useEffect(() => {
    const prefillLead = searchParams.get('prefill_lead');
    if (!prefillLead) return;

    // Query-string fallback değerleri (eski link formatı için)
    const qsAdSoyad = searchParams.get('ad_soyad') || '';
    const qsTelefon = searchParams.get('telefon') || '';
    const qsEmail = searchParams.get('email') || '';

    // Tam lead fetch
    axios
      .get(`${API}/admin/leads/${prefillLead}`, { headers: authHeaders() })
      .then((r) => {
        const l = r.data?.lead || r.data || {};
        const adSoyad = l.ad_soyad || qsAdSoyad;
        // Ad/Soyad bolme: ILK kelime AD, KALANI SOYAD (TR konvansiyonu,
        // AdminStudentOps ile tutarli). Cok kelimeli adlarda son kelimeyi
        // soyad yapma hatasi giderildi. Her iki alan da editlenebilir
        // (kullanici yanlis bolunmeyi duzeltebilir).
        const parts = adSoyad.trim().split(/\s+/).filter(Boolean);
        const ad = parts.length ? parts[0] : '';
        const soyad = parts.length > 1 ? parts.slice(1).join(' ') : '';
        setForm((f) => ({
          ...f,
          ad,
          soyad,
          telefon: l.telefon || qsTelefon,
          email: l.email || qsEmail,
          sehir: l.sehir || f.sehir,
          meslek: l.meslek || f.meslek,
          egitim_durumu: l.egitim_durumu || f.egitim_durumu,
          cinsiyet: l.cinsiyet || f.cinsiyet,
          reklam_turu: l.reklam_turu || f.reklam_turu,
          aciklama: l.aciklama || f.aciklama,
          lead_id: prefillLead,
        }));
      })
      .catch(() => {
        // Fetch başarısız olursa query-string ile devam et
        const parts = qsAdSoyad.trim().split(/\s+/).filter(Boolean);
        const ad = parts.length ? parts[0] : '';
        const soyad = parts.length > 1 ? parts.slice(1).join(' ') : '';
        setForm((f) => ({
          ...f,
          ad,
          soyad,
          telefon: qsTelefon,
          email: qsEmail,
          lead_id: prefillLead,
        }));
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch definitions
  useEffect(() => {
    axios
      .get(`${API}/admin/definitions`, { headers: authHeaders() })
      .then((r) => {
        const all = r.data.definitions || [];
        setDefs({
          meslek: all.filter((d) => d.type === 'meslek'),
          sehir: all.filter((d) => d.type === 'sehir'),
          reklam_turu: all.filter((d) => d.type === 'reklam_turu'),
          egitim_durumu: all.filter((d) => d.type === 'egitim_durumu'),
        });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setF = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      // TC seçimi değişince ilgili no alanını sıfırla
      if (k === 'tc_vatandasi') {
        next.tc_no = '';
        next.pasaport_no = '';
      }
      return next;
    });
    setFieldErrors((e) => {
      const ne = { ...e, [k]: undefined };
      // Vatandaslik modu degisince ESKI modun validasyon hatasi takili kalmasin:
      // hem tc_no hem pasaport_no error'i temizlenir (alanlar da sifirlandi).
      if (k === 'tc_vatandasi') {
        ne.tc_no = undefined;
        ne.pasaport_no = undefined;
      }
      return ne;
    });
    if (k === 'ad' || k === 'soyad' || k === 'tc_no' || k === 'dogum_tarihi') {
      setVerifyResult(null);
    }
  };

  // ── Identity verify ───────────────────────────────────────────────────────

  const canVerify =
    form.tc_vatandasi &&
    form.tc_no.length === 11 &&
    form.ad.trim() &&
    form.soyad.trim() &&
    form.dogum_tarihi;

  const handleVerify = async () => {
    if (!canVerify) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const dogumYili = form.dogum_tarihi ? new Date(form.dogum_tarihi).getFullYear() : undefined;
      const r = await axios.post(
        `${API}/admin/identity/verify`,
        {
          tc_no: form.tc_no,
          ad: form.ad,
          soyad: form.soyad,
          dogum_yili: dogumYili,
        },
        { headers: authHeaders() }
      );
      setVerifyResult(r.data);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setVerifying(false);
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errs = {};
    if (!form.ad.trim()) errs.ad = tr('Zorunlu alan', 'Required field');
    if (!form.soyad.trim()) errs.soyad = tr('Zorunlu alan', 'Required field');
    if (!form.telefon.trim()) errs.telefon = tr('Zorunlu alan', 'Required field');
    if (!form.email.trim()) errs.email = tr('Zorunlu alan', 'Required field');
    if (!form.telefon2.trim()) errs.telefon2 = tr('Zorunlu alan', 'Required field');
    if (!form.egitim_durumu.trim()) errs.egitim_durumu = tr('Zorunlu alan', 'Required field');
    if (!form.sehir.trim()) errs.sehir = tr('Zorunlu alan', 'Required field');
    if (!form.meslek.trim()) errs.meslek = tr('Zorunlu alan', 'Required field');
    if (!form.dogum_tarihi.trim()) errs.dogum_tarihi = tr('Zorunlu alan', 'Required field');
    if (!form.dogum_yeri.trim()) errs.dogum_yeri = tr('Zorunlu alan', 'Required field');
    if (form.tc_vatandasi && !form.tc_no.trim()) errs.tc_no = tr('Zorunlu alan', 'Required field');
    if (!form.tc_vatandasi && !form.pasaport_no.trim()) errs.pasaport_no = tr('Zorunlu alan', 'Required field');

    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      toast.error(tr('Lütfen zorunlu alanları doldurun', 'Please fill required fields'));
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        ad: form.ad,
        soyad: form.soyad,
        telefon: form.telefon,
        email: form.email,
        telefon2: form.telefon2,
        egitim_durumu: form.egitim_durumu,
        sehir: form.sehir,
        meslek: form.meslek,
        dogum_tarihi: form.dogum_tarihi,
        dogum_yeri: form.dogum_yeri,
        tc_vatandasi: form.tc_vatandasi,
        anne_adi: form.anne_adi || undefined,
        baba_adi: form.baba_adi || undefined,
        cinsiyet: form.cinsiyet || undefined,
        yurtdisi_no: form.yurtdisi_no || undefined,
        yurtdisi_tel: form.yurtdisi_tel,
        adres: form.adres || undefined,
        lisans_bolumu: form.lisans_bolumu || undefined,
        egitim_aciklama: form.egitim_aciklama || undefined,
        reklam_turu: form.reklam_turu || undefined,
        ogrenci_kodu_ozel: form.ogrenci_kodu_ozel || undefined,
        aktiflik_suresi: form.aktiflik_suresi || undefined,
        aciklama: form.aciklama || undefined,
        pasif: form.pasif,
        fatura_talebi: form.fatura_talebi,
        lead_id: form.lead_id || undefined,
      };
      if (form.tc_vatandasi && form.tc_no) {
        body.tc_no = form.tc_no;
      } else if (!form.tc_vatandasi && form.pasaport_no) {
        body.pasaport_no = form.pasaport_no;
      }

      const r = await axios.post(`${API}/admin/students/create-identity`, body, {
        headers: authHeaders(),
      });

      setSuccessResult(r.data);
      toast.success(tr('Kimlik oluşturuldu!', 'Identity created!'));
    } catch (err) {
      const code = getApiErrorCode(err);
      const msg = getApiErrorMessage(err, lang);

      if (code === 'telefon_mevcut') {
        setFieldErrors((fe) => ({ ...fe, telefon: msg }));
        toast.error(msg);
      } else if (code === 'email_mevcut') {
        setFieldErrors((fe) => ({ ...fe, email: msg }));
        toast.error(msg);
      } else if (code === 'gecersiz_tc_no') {
        setFieldErrors((fe) => ({ ...fe, tc_no: msg }));
        toast.error(msg);
      } else if (code === 'tc_no_mevcut') {
        setFieldErrors((fe) => ({ ...fe, tc_no: msg }));
        toast.error(msg);
      } else if (code === 'pasaport_no_mevcut') {
        setFieldErrors((fe) => ({ ...fe, pasaport_no: msg }));
        toast.error(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewRecord = () => {
    setForm(EMPTY_FORM);
    setFieldErrors({});
    setSuccessResult(null);
    setVerifyResult(null);
  };

  // ── Success screen ────────────────────────────────────────────────────────

  if (successResult) {
    return (
      <AdminLayout>
        <PageHeader
          breadcrumbs={[
            { label: 'Admin', href: '/admin' },
            { label: tr('Öğrenciler', 'Students'), href: '/admin/students' },
            { label: tr('Yeni Kimlik', 'New Identity') },
          ]}
          title={tr('Kimlik Oluşturuldu', 'Identity Created')}
        />
        <SuccessCard
          result={successResult}
          lang={lang}
          authHeaders={authHeaders}
          onNewRecord={handleNewRecord}
          navigate={navigate}
        />
      </AdminLayout>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────────────

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: tr('Öğrenciler', 'Students'), href: '/admin/students' },
          { label: tr('Yeni Kimlik Oluştur', 'New Identity') },
        ]}
        title={tr('Yeni Kimlik Oluştur', 'Create New Identity')}
        subtitle={tr(
          'Yeni öğrenci kimliği ve sistem kaydı oluşturun',
          'Create a new student identity and system record'
        )}
      />

      {form.lead_id && (
        <div className="mb-4 flex items-center gap-2 px-4 py-2.5 bg-cyan-50 border-2 border-cyan-200 rounded-xl text-cyan-700 text-sm font-semibold">
          <ShieldCheck size={16} />
          {tr(
            `Lead #${form.lead_id} verilerinden ön doldurma yapıldı`,
            `Pre-filled from lead #${form.lead_id}`
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* ── Bölüm 1: Kimlik ─────────────────────────────────────────── */}
        <div className={SECTION_CLS}>
          <h3 className="font-bold text-sm text-[var(--text-main)] flex items-center gap-2">
            <UserSquare2 size={16} className="text-[var(--brand-primary)]" />
            {tr('Kimlik Bilgileri', 'Identity Information')}
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label={tr('Ad', 'First Name')} required error={fieldErrors.ad}>
              <input
                className={INPUT_CLS + (fieldErrors.ad ? ' border-rose-400 bg-rose-50' : '')}
                value={form.ad}
                onChange={(e) => setF('ad', e.target.value)}
              />
            </Field>
            <Field label={tr('Soyad', 'Last Name')} required error={fieldErrors.soyad}>
              <input
                className={INPUT_CLS + (fieldErrors.soyad ? ' border-rose-400 bg-rose-50' : '')}
                value={form.soyad}
                onChange={(e) => setF('soyad', e.target.value)}
              />
            </Field>

            <Field label={tr('Cinsiyet', 'Gender')}>
              <select
                className={INPUT_CLS}
                value={form.cinsiyet}
                onChange={(e) => setF('cinsiyet', e.target.value)}
              >
                <option value="">{tr('Seçiniz', 'Select')}</option>
                <option value="Erkek">{tr('Erkek', 'Male')}</option>
                <option value="Kadin">{tr('Kadın', 'Female')}</option>
                <option value="Diger">{tr('Diğer', 'Other')}</option>
              </select>
            </Field>
          </div>

          {/* TC vatandası radyo */}
          <div>
            <label className={LBL_CLS}>{tr('Vatandaşlık', 'Citizenship')}</label>
            <div className="flex gap-4">
              {[
                { val: true, label: tr('T.C. Vatandaşı', 'Turkish Citizen') },
                { val: false, label: tr('Diğer / Yabancı', 'Other / Foreign') },
              ].map(({ val, label }) => (
                <label key={String(val)} className="flex items-center gap-2 cursor-pointer text-sm font-semibold">
                  <input
                    type="radio"
                    name="tc_vatandasi"
                    checked={form.tc_vatandasi === val}
                    onChange={() => setF('tc_vatandasi', val)}
                    className="accent-[var(--brand-primary)]"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {form.tc_vatandasi ? (
              <Field label={tr('T.C. Kimlik No', 'TC ID No')} required error={fieldErrors.tc_no}>
                <input
                  className={INPUT_CLS + (fieldErrors.tc_no ? ' border-rose-400 bg-rose-50' : '')}
                  value={form.tc_no}
                  onChange={(e) => setF('tc_no', e.target.value.replace(/\D/g, '').slice(0, 11))}
                  maxLength={11}
                  placeholder="11 hane"
                />
              </Field>
            ) : (
              <Field label={tr('Pasaport No', 'Passport No')} required error={fieldErrors.pasaport_no}>
                <input
                  className={INPUT_CLS + (fieldErrors.pasaport_no ? ' border-rose-400 bg-rose-50' : '')}
                  value={form.pasaport_no}
                  onChange={(e) => setF('pasaport_no', e.target.value)}
                  placeholder={tr('Pasaport numaranızı girin', 'Enter passport number')}
                />
              </Field>
            )}

            <Field label={tr('Doğum Tarihi', 'Birth Date')} required error={fieldErrors.dogum_tarihi}>
              <input
                type="date"
                className={INPUT_CLS + (fieldErrors.dogum_tarihi ? ' border-rose-400 bg-rose-50' : '')}
                value={form.dogum_tarihi}
                onChange={(e) => setF('dogum_tarihi', e.target.value)}
              />
            </Field>

            <Field label={tr('Doğum Yeri', 'Birth Place')} required error={fieldErrors.dogum_yeri}>
              <input
                className={INPUT_CLS + (fieldErrors.dogum_yeri ? ' border-rose-400 bg-rose-50' : '')}
                value={form.dogum_yeri}
                onChange={(e) => setF('dogum_yeri', e.target.value)}
              />
            </Field>

            <Field label={tr('Anne Adı', "Mother's Name")}>
              <input
                className={INPUT_CLS}
                value={form.anne_adi}
                onChange={(e) => setF('anne_adi', e.target.value)}
              />
            </Field>

            <Field label={tr('Baba Adı', "Father's Name")}>
              <input
                className={INPUT_CLS}
                value={form.baba_adi}
                onChange={(e) => setF('baba_adi', e.target.value)}
              />
            </Field>
          </div>

          {/* Identity verify button */}
          {form.tc_vatandasi && (
            <div className="flex items-center gap-3 pt-1">
              <motion.button
                type="button"
                onClick={handleVerify}
                disabled={!canVerify || verifying}
                whileHover={canVerify && !verifying ? { scale: 1.03 } : {}}
                whileTap={canVerify && !verifying ? { scale: 0.97 } : {}}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-[var(--brand-primary)] text-[var(--brand-primary)] text-sm font-bold hover:bg-cyan-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {verifying ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <ShieldCheck size={14} />
                )}
                {tr('Kimliği Doğrula', 'Verify Identity')}
              </motion.button>

              {!canVerify && (
                <p className="text-[10px] text-[var(--text-muted)] font-semibold">
                  {tr(
                    'TC No (11 hane), Ad, Soyad ve Doğum Tarihi gerekli',
                    'TC No (11 digits), First Name, Last Name and Birth Date required'
                  )}
                </p>
              )}

              <AnimatePresence>
                {verifyResult && (
                  <motion.div
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border-2 ${
                      verifyResult.verified
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                        : 'bg-rose-50 border-rose-200 text-rose-700'
                    }`}
                  >
                    {verifyResult.verified ? (
                      <>
                        <CheckCircle2 size={13} />
                        {tr('Doğrulandı', 'Verified')}
                        {verifyResult.method && (
                          <span className="text-[10px] font-normal opacity-70">({verifyResult.method})</span>
                        )}
                      </>
                    ) : (
                      <>
                        <AlertTriangle size={13} />
                        {verifyResult.reason || tr('Doğrulanamadı', 'Could not verify')}
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* ── Bölüm 2: İletişim ──────────────────────────────────────── */}
        <div className={SECTION_CLS}>
          <h3 className="font-bold text-sm text-[var(--text-main)]">
            {tr('İletişim Bilgileri', 'Contact Information')}
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label={tr('E-posta', 'E-mail')} required error={fieldErrors.email}>
              <input
                type="email"
                className={INPUT_CLS + (fieldErrors.email ? ' border-rose-400 bg-rose-50' : '')}
                value={form.email}
                onChange={(e) => setF('email', e.target.value)}
              />
            </Field>

            <Field label={tr('Cep Telefonu', 'Mobile Phone')} required error={fieldErrors.telefon}>
              <input
                className={INPUT_CLS + (fieldErrors.telefon ? ' border-rose-400 bg-rose-50' : '')}
                value={form.telefon}
                onChange={(e) => setF('telefon', e.target.value)}
                placeholder="05xx xxx xx xx"
              />
            </Field>

            <Field label={tr('Ek İrtibat Telefonu', 'Additional Contact Phone')} required error={fieldErrors.telefon2}>
              <input
                className={INPUT_CLS + (fieldErrors.telefon2 ? ' border-rose-400 bg-rose-50' : '')}
                value={form.telefon2}
                onChange={(e) => setF('telefon2', e.target.value)}
                placeholder="05xx xxx xx xx"
              />
            </Field>

            <Field label={tr('Şehir', 'City')} required error={fieldErrors.sehir}>
              <select
                className={INPUT_CLS + (fieldErrors.sehir ? ' border-rose-400 bg-rose-50' : '')}
                value={form.sehir}
                onChange={(e) => setF('sehir', e.target.value)}
              >
                <option value="">{tr('Seçiniz', 'Select')}</option>
                {defs.sehir.map((d) => (
                  <option key={d.value || d._id} value={d.value || d.name}>{d.name || d.value}</option>
                ))}
              </select>
            </Field>

            <Field label={tr('Yurt Dışı Telefon', 'International Phone')}>
              <input
                className={INPUT_CLS}
                value={form.yurtdisi_no}
                onChange={(e) => setF('yurtdisi_no', e.target.value)}
                placeholder="+1 xxx xxx xxxx"
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold">
            <input
              type="checkbox"
              checked={form.yurtdisi_tel}
              onChange={(e) => setF('yurtdisi_tel', e.target.checked)}
              className="w-4 h-4 rounded accent-[var(--brand-primary)]"
            />
            {tr('Yurt Dışı Numarası Var', 'Has International Number')}
          </label>

          <Field label={tr('Adres', 'Address')}>
            <textarea
              className={INPUT_CLS}
              rows={2}
              value={form.adres}
              onChange={(e) => setF('adres', e.target.value)}
            />
          </Field>
        </div>

        {/* ── Bölüm 3: Eğitim & Meslek ───────────────────────────────── */}
        <div className={SECTION_CLS}>
          <h3 className="font-bold text-sm text-[var(--text-main)]">
            {tr('Eğitim & Meslek', 'Education & Profession')}
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label={tr('Meslek', 'Profession')} required error={fieldErrors.meslek}>
              <select
                className={INPUT_CLS + (fieldErrors.meslek ? ' border-rose-400 bg-rose-50' : '')}
                value={form.meslek}
                onChange={(e) => setF('meslek', e.target.value)}
              >
                <option value="">{tr('Seçiniz', 'Select')}</option>
                {defs.meslek.map((d) => (
                  <option key={d.value || d._id} value={d.value || d.name}>{d.name || d.value}</option>
                ))}
              </select>
            </Field>

            <Field label={tr('Eğitim Durumu', 'Education Level')} required error={fieldErrors.egitim_durumu}>
              <select
                className={INPUT_CLS + (fieldErrors.egitim_durumu ? ' border-rose-400 bg-rose-50' : '')}
                value={form.egitim_durumu}
                onChange={(e) => setF('egitim_durumu', e.target.value)}
              >
                <option value="">{tr('Seçiniz', 'Select')}</option>
                {defs.egitim_durumu.length > 0
                  ? defs.egitim_durumu.map((d) => (
                      <option key={d.value || d._id} value={d.value || d.name}>{d.name || d.value}</option>
                    ))
                  : ['İlköğretim', 'Lise', 'Ön Lisans', 'Lisans', 'Yüksek Lisans', 'Doktora'].map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
              </select>
            </Field>

            <Field label={tr('Lisans Bölümü', 'University Department')}>
              <input
                className={INPUT_CLS}
                value={form.lisans_bolumu}
                onChange={(e) => setF('lisans_bolumu', e.target.value)}
              />
            </Field>
          </div>

          <Field label={tr('Eğitim Açıklama', 'Education Notes')}>
            <textarea
              className={INPUT_CLS}
              rows={2}
              value={form.egitim_aciklama}
              onChange={(e) => setF('egitim_aciklama', e.target.value)}
            />
          </Field>
        </div>

        {/* ── Bölüm 4: Sistem ────────────────────────────────────────── */}
        <div className={SECTION_CLS}>
          <h3 className="font-bold text-sm text-[var(--text-main)]">
            {tr('Sistem Ayarları', 'System Settings')}
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label={tr('Reklam Türü', 'Ad Source')}>
              <select
                className={INPUT_CLS}
                value={form.reklam_turu}
                onChange={(e) => setF('reklam_turu', e.target.value)}
              >
                <option value="">{tr('Seçiniz', 'Select')}</option>
                {defs.reklam_turu.map((d) => (
                  <option key={d.value || d._id} value={d.value || d.name}>{d.name || d.value}</option>
                ))}
              </select>
            </Field>

            <Field label={tr('Özel Öğrenci Kodu', 'Custom Student Code')}>
              <input
                className={INPUT_CLS}
                value={form.ogrenci_kodu_ozel}
                onChange={(e) => setF('ogrenci_kodu_ozel', e.target.value)}
                placeholder={tr('Boş bırakılırsa otomatik', 'Auto if empty')}
              />
            </Field>

            <Field
              label={tr('Aktiflik Süresi', 'Active Until')}
              hint={tr(
                'Boş bırakılabilir — eğitime göre satışta belirlenir',
                'Optional — determined at sale based on program'
              )}
            >
              <input
                type="date"
                className={INPUT_CLS}
                value={form.aktiflik_suresi}
                onChange={(e) => setF('aktiflik_suresi', e.target.value)}
              />
            </Field>
          </div>

          <Field label={tr('Açıklama', 'Notes')}>
            <textarea
              className={INPUT_CLS}
              rows={2}
              value={form.aciklama}
              onChange={(e) => setF('aciklama', e.target.value)}
            />
          </Field>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold">
              <input
                type="checkbox"
                checked={form.pasif}
                onChange={(e) => setF('pasif', e.target.checked)}
                className="w-4 h-4 rounded accent-amber-500"
              />
              {tr('Pasif Öğrenci', 'Inactive Student')}
            </label>

            <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold">
              <input
                type="checkbox"
                checked={form.fatura_talebi}
                onChange={(e) => setF('fatura_talebi', e.target.checked)}
                className="w-4 h-4 rounded accent-[var(--brand-primary)]"
              />
              {tr('Fatura Talebi', 'Invoice Request')}
            </label>
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-end gap-3 pb-6">
          <Link
            to="/admin/students"
            className="px-5 py-2.5 rounded-xl border-2 border-[var(--border-light)] text-sm font-bold hover:bg-gray-50"
          >
            {tr('İptal', 'Cancel')}
          </Link>
          <motion.button
            type="submit"
            disabled={submitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold border-b-4 border-[var(--brand-primary-shadow)] disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <CheckCircle2 size={15} />
            )}
            {submitting
              ? tr('Kaydediliyor...', 'Saving...')
              : tr('Kimliği Oluştur', 'Create Identity')}
          </motion.button>
        </div>
      </form>
    </AdminLayout>
  );
}
